import Icon from '../components/Icon';
import React, { useContext, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { GameContext } from '../components/GameContext';
import { addPlayerData, checkIfNameExists, checkIfPromptExistsForOthers, updatePlayerData } from '../components/utils/dbUtils';
import { checkIfGameCodeExists } from '../components/pre_game/GameCodeUtils';
import expansion from '../expansion';
import { readSaved, save } from '../gameDeck';
import { CreativeTVLink } from '../components/CreativeDisplayBridge';
import AvatarPicker from '../components/AvatarPicker';
import { cleanIdentity, identityFor } from '../playerIdentity';

const pendingJoins = new Map();
async function getMembership(code) {
  const stored = readSaved(code + '_data', null);
  if (stored?.playerId) return stored;
  if (!pendingJoins.has(code)) {
    pendingJoins.set(code, (async () => {
      const playerId = await addPlayerData(code, { name: '', prompts: [], isHost: false, isAddingPrompts: true, isReady: false });
      const membership = { gameId: code, playerId, isHost: false, creationTimestamp: new Date().toISOString() };
      save(code + '_data', membership);
      return membership;
    })().finally(() => pendingJoins.delete(code)));
  }
  return pendingJoins.get(code);
}

export default function CreativePrompts() {
  const { urlGameCode: code } = useParams();
  const { setGameCode, setPlayerId, setIsReady } = useContext(GameContext);
  const navigate = useNavigate();
  const [membership, setMembership] = useState(null);
  const [name, setName] = useState('');
  const [prompts, setPrompts] = useState(['', '', '']);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [idea, setIdea] = useState('');
  const [identity, setIdentity] = useState({});
  const [photoBusy, setPhotoBusy] = useState(false);
  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const result = await checkIfGameCodeExists(code);
        if (!result.exists || result.expiredCode) throw new Error('This room is missing or has expired. Ask your host for a new code.');
        const member = await getMembership(code);
        const snapshot = await getDoc(doc(db, 'gameSessions', code, 'players', member.playerId));
        if (!snapshot.exists()) throw new Error('Your player could not be found. Return home and join a new room.');
        if (cancelled) return;
        const player = snapshot.data();
        const draft = readSaved(`diyt-draft-${code}-${member.playerId}`, null);
        setMembership(member); setGameCode(code); setPlayerId(member.playerId);
        setName(draft?.name ?? player.name ?? '');
        setIdentity(identityFor({ playerId: member.playerId, ...(draft?.identity ?? cleanIdentity(player)) }));
        setPrompts([0, 1, 2].map(i => draft?.prompts?.[i] ?? player.prompts?.[i] ?? ''));
      } catch (e) { if (!cancelled) setError(e.code ? 'Couldn’t connect to the room. Check your connection and reload to try again.' : e.message); }
      finally { if (!cancelled) setLoading(false); }
    }
    load();
    return () => { cancelled = true; };
  }, [code, setGameCode, setPlayerId]);
  useEffect(() => { if (membership) save(`diyt-draft-${code}-${membership.playerId}`, { name, prompts, identity }); }, [membership, code, name, prompts, identity]);
  async function submit(e) {
    e.preventDefault();
    const cleanName = name.trim();
    const cleanPrompts = prompts.map(p => p.trim().replace(/^drink if you think\s*/i, ''));
    if (!cleanName || cleanPrompts.some(p => !p)) { setError('Add your name and three complete prompts.'); return; }
    if (new Set(cleanPrompts.map(p => p.toLowerCase())).size !== 3) { setError('Each of your three prompts should be different.'); return; }
    setSaving(true); setError('');
    try {
      if (await checkIfNameExists(cleanName, code, membership.playerId)) throw new Error('That name is already taken. Try a nickname.');
      const duplicates = await Promise.all(cleanPrompts.map(p => checkIfPromptExistsForOthers(p, code, membership.playerId)));
      if (duplicates.some(Boolean)) throw new Error(`Someone already used prompt ${duplicates.findIndex(Boolean) + 1}. Give it a different twist.`);
      await updatePlayerData(cleanName, cleanPrompts, code, membership.playerId, true, identity);
      setIsReady(true);
      navigate(`/creative-mode/${code}/${membership.isHost ? 'waiting-lobby' : 'close-phone'}`);
    } catch (e) { setError(e.code ? 'Your prompts haven’t been submitted yet. Check your connection and try again.' : e.message); }
    finally { setSaving(false); }
  }
  return <form className="creative-form" onSubmit={submit}>
    {membership?.isHost && <CreativeTVLink code={code} />}
    <span className="eyebrow">CREATIVE MODE · ROOM {code}</span><h1>Make it personal.</h1><p>Write three prompts, keep them secret, and see who can guess they’re yours. Finish the sentence “Drink if you think…”</p>
    {loading ? <p role="status">Joining your table…</p> : membership && <>
      <AvatarPicker value={{ ...identity, name }} onChange={setIdentity} disabled={saving} onBusyChange={setPhotoBusy} />
      <label htmlFor="creative-name">What should we call you?</label><input id="creative-name" placeholder="Your name" value={name} maxLength={32} required onChange={e => setName(e.target.value)} />
      {prompts.map((prompt, i) => <div key={i}><label htmlFor={`creative-prompt-${i}`}>{i + 1}. Drink if you think…</label><textarea id={`creative-prompt-${i}`} rows={2} placeholder={['you would survive longer on a deserted island.', 'you are the better secret keeper.', 'you would win a karaoke battle.'][i]} value={prompt} maxLength={240} required onChange={e => setPrompts(p => p.map((v, j) => i === j ? e.target.value : v))} /></div>)}
      <button type="button" className="idea-button" onClick={() => setIdea(expansion[Math.floor(Math.random() * expansion.length)].description)}>Need inspiration? <Icon name="arrow-right" /></button>
      {idea && <p className="inline-rules">{idea} <em>Give it your own twist.</em></p>}
      <button className="button dark full" disabled={saving || photoBusy}>{saving ? 'Saving your prompts…' : 'I’m ready'}<Icon name="arrow-right" /></button>
    </>}
    {error && <p role="alert" className="error-message">{error}</p>}<Link className="text-link" to="/">Back home</Link>
  </form>;
}
